import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    let subscription: any = null;
    
    // Timeout de segurança: 5 segundos
    const timeoutId = setTimeout(() => {
      if (isMounted && loading) {
        console.warn('[Auth] Timeout atingido, assumindo não autenticado');
        setLoading(false);
        setUser(null);
        setSession(null);
      }
    }, 5000);

    // Função para atualizar estado de forma segura
    const updateAuthState = (session: Session | null) => {
      if (!isMounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      clearTimeout(timeoutId);
    };

    // PRIMEIRO: Verificar sessão existente
    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        if (error) {
          console.error('[Auth] Erro ao buscar sessão:', error);
          if (isMounted) {
            setLoading(false);
            setUser(null);
            setSession(null);
            clearTimeout(timeoutId);
          }
          return;
        }
        
        console.log('[Auth] getSession resultado:', { hasSession: !!session });
        updateAuthState(session);
        
        // DEPOIS: Configurar listener apenas se não houve erro
        const { data: { subscription: sub } } = supabase.auth.onAuthStateChange(
          (event, session) => {
            console.log('[Auth] onAuthStateChange:', { event, hasSession: !!session });
            updateAuthState(session);
          }
        );
        
        subscription = sub;
      })
      .catch((error) => {
        console.error('[Auth] Erro crítico ao inicializar auth:', error);
        if (isMounted) {
          setLoading(false);
          setUser(null);
          setSession(null);
          clearTimeout(timeoutId);
        }
      });

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};